'use strict';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import {observeNewOnScope} from '../../core-utilities/rx-utils';
import {ACL_UPDATE_TTL} from './PermissionsConfig';

/**
 * Callback when a user's role changes
 *
 * @callback onRolesUpdate
 * @param {Role} newRole
 */

/**
 * Return the appropriate model
 *
 * @callback modelGetter
 * @returns {Object} model
 */

/**
 * Return the appropriate model's ID
 *
 * @callback modelIdGetter
 * @returns {string} modelId
 */

/**
 * Retrieve the role corresponding to the model
 *
 * Examples:
 *   if the model is an OrganizationDomain, use $permissions.retrieveRole
 *   if the model is a ProjectDomain, use $permissions.retrieveProjectRole
 *
 * @callback roleGetter
 * @param {String} roleId
 * @returns {Promise.<Role|*>} modelId
 */

export class RoleBinding {
  static updateAclKey(userId, targetId) {
    return userId + ':' + targetId;
  }

  /**
   * Supports legacy uses of this class
   * @param {$rootScope.Scope} $scope
   * @param {modelIdGetter|string} modelIdGetter
   * @return {*}
   */
  static legacyToObservable($scope, modelIdGetter) {
    if (isFunction(modelIdGetter)) {
      return observeNewOnScope($scope, modelIdGetter);
    } else if (isString(modelIdGetter)) {
      return Observable.of(modelIdGetter);
    } else {
      return Observable.empty();
    }
  }

  /**
   * Supports legacy uses of this class
   * @param {$rootScope.Scope} $scope
   * @param {modelGetter|string} modelGetter
   * @return {*}
   */
  static legacyModelToObservable($scope, modelGetter) {
    if (isFunction(modelGetter)) {
      return observeNewOnScope($scope, modelGetter);
    } else if (isObject(modelGetter)) {
      return Observable.of(modelGetter);
    } else {
      return Observable.empty();
    }
  }

  /**
   * @param {Observable<String>} modelId$
   * @param {roleGetter} roleGetter
   * @param {HasMembersDomainClass} domainClass
   * @param {AuthService} AuthService
   * @param {$domain} $domain
   * @param {$permissionsVersions} $permissionsVersions
   * @param {Cache} aclCache
   */
  constructor(modelId$, roleGetter, domainClass, AuthService, $domain, $permissionsVersions, aclCache) {
    this.modelId$ = modelId$;
    this.roleGetter = roleGetter;
    this.domainClass = domainClass;
    this.AuthService = AuthService;
    this.$domain = $domain;
    this.$permissionsVersions = $permissionsVersions;
    this.aclCache = aclCache;
  }

  role$() {
    const self = this;
    return this.modelId$
      .distinctUntilChanged()
      .switchMap(id => id && isString(id) ? this.observeRef(id).switchMap(update) : Observable.of(null));

    /**
     * @param {FirebaseDataSnapshot} ss
     */
    function update(ss) {
      if (!ss.exists()) {
        return Observable.of(null);
      } else {
        const member = ss.val();
        const roleId = get(member, 'role.id');
        if (roleId) {

          // prompt the server to update the user's firebase acl permissions
          const id = self.$domain.idFromRelationRef(ss.ref());
          const aclCacheId = RoleBinding.updateAclKey(self.AuthService.getUserId(), id);
          if (self.aclCache.get(aclCacheId) !== roleId) {
            self.aclCache.set(aclCacheId, roleId, ACL_UPDATE_TTL);
            self.$permissionsVersions.loadedPrivateData(self.domainClass.COLLECTION, id).catch(e => {
              console.error(e);
            });
          }

          return self.roleGetter(roleId);
        } else {
          return Observable.of(null);
        }
      }
    }
  }

  /**
   *
   * @param {string} id
   * @returns {Observable<FirebaseDataSnapshot>}
   * @private
   */
  observeRef(id) {
    return this.domainClass.$getMembersRelationRef(id, this.AuthService.getUserId())
      .observe('value');
  }
}



// WEBPACK FOOTER //
// ./src/js/auth/services/RoleBinding.js